import * as React from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { useDebounce } from '../hooks/useDebounce'
import LoadingIcon from './LoadingIcon'

export type ButtonSize = 'large' | 'medium' | 'small' | 'icon'
export type ButtonVariants = 'primary' | 'secondary' | 'tertiary' | 'outline' | 'tertiary-outline' | 'error' | 'text'
export type ButtonProps = {
  variant?: ButtonVariants
  size?: ButtonSize
  disabled?: boolean
  loading?: boolean
  loadingDelay?: number
  as?: React.ElementType
} & React.ButtonHTMLAttributes<HTMLButtonElement>

type StyleProps = {
  variant?: ButtonVariants
  size?: ButtonSize
  functionallyLoading?: boolean
  visuallyLoading?: boolean
  functionallyDisabled?: boolean
  visuallyDisabled?: boolean
}

export const DEFAULT_BUTTON_SIZE: ButtonSize = 'large'

const TWENTY_PERCENT_OPACITY = '33'

const Container = styled('button')<StyleProps>`
  position: relative;
  display: grid;
  background: transparent;
  border: 0;
  width: 100%;
  padding: 0;
  outline: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  ${props => {
    switch (props.size) {
      case 'medium':
      case 'small':
      case 'icon':
        return css`
          width: auto;
        `
      default:
        return
    }
  }}

  ${props =>
    props.visuallyLoading &&
    css`
      cursor: default;
    `}

    ${props =>
    props.visuallyDisabled &&
    css`
      cursor: not-allowed;
    `}

    /*
     * Generates outline using pseudo-element for Safari compatibility
     * Safari outline bug details here: https://bugs.webkit.org/show_bug.cgi?id=20807
     */
  ${props => props.theme.selectors.keyboard} &:focus::before {
    content: '';
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
    bottom: -0.5rem;
    left: -0.5rem;
    border: 0.5rem solid ${props => props.theme.colors.primary}${TWENTY_PERCENT_OPACITY};
    border-radius: 3rem;
  }
`

export const StyledButton = styled('span')<StyleProps>`
  background: ${props => props.theme.colors.primaryButtonBackground};
  border-radius: 1.5rem;
  box-sizing: border-box;
  color: ${props => props.theme.colors.primaryButtonTint};
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: inherit;
  text-align: center;
  letter-spacing: 0.01em;
  padding: 0 1rem;
  height: 3rem;
  max-height: 3rem;

  ${props => props.theme.typography.buttonText};

  ${props => props.theme.motion.standardTransition}
  transition-property: transform, background, color, opacity;

  ${({ visuallyDisabled, theme }) =>
    visuallyDisabled &&
    css`
      color: ${theme.colors.disabled};
      cursor: not-allowed;
    `}

  ${({ functionallyDisabled, theme, size }) =>
    !functionallyDisabled &&
    css`
      @media (hover: hover) {
        :hover {
          background: ${theme.colors.primaryButtonHover};
        }
      }
      :active {
        transform: scale(${size === 'small' || size === 'icon' ? 0.95 : 0.98});
        background: ${theme.colors.primaryButtonActive};
      }
    `}

    ${({ size }) => {
    switch (size) {
      case 'small':
        return css`
          font-size: 1rem;
          padding: 0.25rem 1rem;
          height: auto;
          max-height: auto;
        `
      case 'icon':
        return css`
          padding: 0.25rem;
          font-size: 1.5rem;
          height: auto;
          max-height: auto;
          @media (hover: hover) {
            :hover {
              opacity: 0.75;
              background: transparent;
            }
          }
          :active {
            opacity: 0.5;
            background: transparent;
          }
        `
      default:
        return
    }
  }}

  ${({ functionallyDisabled, visuallyDisabled, theme, variant }) => {
    switch (variant) {
      case 'secondary':
        return css`
          color: ${theme.colors.secondaryButtonTint};
          background: ${theme.colors.secondaryButtonBackground};

          ${visuallyDisabled &&
          css`
            color: ${theme.colors.disabledLabel};
          `}

          ${!functionallyDisabled &&
          css`
            @media (hover: hover) {
              :hover {
                background: ${theme.colors.secondaryButtonHover};
              }
            }
            :active {
              background: ${theme.colors.secondaryButtonActive};
            }
          `}
        `
      case 'tertiary':
        return css`
          color: ${theme.colors.primaryButtonBackground};
          background: transparent;

          ${visuallyDisabled &&
          css`
            color: ${theme.colors.disabledLabel};
          `}

          ${!functionallyDisabled &&
          css`
            @media (hover: hover) {
              :hover {
                color: ${theme.colors.primaryButtonHover};
                background: transparent;
              }
            }
            :active {
              color: ${theme.colors.primaryButtonActive};
              background: transparent;
            }
          `}
        `
      case 'outline':
        return css`
          color: ${theme.colors.secondaryButtonTint};
          background: transparent;
          border: 2px solid;
          border-color: ${theme.colors.outline};

          ${visuallyDisabled &&
          css`
            color: ${theme.colors.disabledLabel};
          `}

          ${!functionallyDisabled &&
          css`
            @media (hover: hover) {
              :hover {
                background: ${theme.colors.secondaryButtonHover};
                border-color: transparent;
              }
            }
            :active {
              background: ${theme.colors.secondaryButtonActive};
              border-color: transparent;
            }
          `}
        `
      case 'tertiary-outline':
        return css`
          color: ${theme.colors.tertiaryButtonTint};
          background: transparent;
          border: 2px solid;
          border-color: ${theme.colors.tertiaryButtonTint};

          ${visuallyDisabled &&
          css`
            color: ${theme.colors.disabledLabel};
          `}

          ${!functionallyDisabled &&
          css`
            @media (hover: hover) {
              :hover {
                color: ${theme.colors.primaryButtonHover};
                background: transparent;
                border-color: ${theme.colors.primaryButtonHover};
              }
            }
            :active {
              color: ${theme.colors.primaryButtonActive};
              background: transparent;
              border-color: ${theme.colors.primaryButtonActive};
            }
          `}
        `
      case 'error':
        return css`
          color: ${theme.colors.primaryButtonTint};
          background: ${theme.colors.error};

          ${visuallyDisabled &&
          css`
            color: rgba(255, 255, 255, 0.5);
          `}

          ${!functionallyDisabled &&
          css`
            @media (hover: hover) {
              :hover {
                background: ${theme.colors.errorHover};
              }
            }
            :active {
              background: ${theme.colors.errorPressed};
            }
          `}
        `
      case 'text':
        return css`
          color: ${theme.colors.secondaryButtonTint};
          background: none;
          font-weight: normal;
          font-size: 1rem;
          ${visuallyDisabled &&
          css`
            color: ${theme.colors.disabledLabel};
          `}

          ${!functionallyDisabled &&
          css`
            @media (hover: hover) {
              :hover {
                background: ${theme.colors.secondaryButtonHover};
              }
            }
            :active {
              background: ${theme.colors.secondaryButtonActive};
            }
          `}
        `
      default:
        return
    }
  }}
`

// Keep button container to avoid unclickable edges when using this component as an `a` tag caused by `scale` property
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant, disabled, loading, loadingDelay = 0, size = DEFAULT_BUTTON_SIZE, ...rest }, ref) => {
    const functionallyLoading = loading
    const visuallyLoading = useDebounce(loading, loading ? loadingDelay : 0)

    const functionallyDisabled = disabled || loading
    const visuallyDisabled = disabled

    const styleProps: StyleProps = {
      variant,
      size,
      functionallyLoading,
      visuallyLoading,
      functionallyDisabled,
      visuallyDisabled,
    }

    return (
      <Container ref={ref} {...styleProps} {...rest} disabled={functionallyDisabled}>
        <StyledButton {...styleProps}>
          {visuallyLoading ? <LoadingIcon size="1.5rem" color="current" /> : children}
        </StyledButton>
      </Container>
    )
  }
)

export default Button
